export const EMAIL_ALREADY_EXISTS_CODE = "EMAIL_ALREADY_EXISTS";
export const EMAIL_ALREADY_EXISTS_MESSAGE = "המייל כבר קיים במערכת";
export const BUDGET_LINE_ACCOUNT_SELECTION_REQUIRED_CODE = "BUDGET_LINE_ACCOUNT_SELECTION_REQUIRED";
export const BUDGET_LINE_ACCOUNT_SELECTION_GUIDANCE = "לבחירת החשבון: מעקב ופערים ← השורה התקציבית של הקטגוריה ← חשבון היעד לשורה, או חלוקה בין חשבונות שמסתכמת ב־100%.";

type ApiErrorPayload = {
    code?: unknown;
    message?: unknown;
    error?: unknown;
    details?: unknown;
    retryable?: unknown;
};

const nonEmptyString = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    const normalized = value.trim();
    return normalized || undefined;
};

const asErrorPayload = (value: unknown): ApiErrorPayload | null => {
    if (!value || typeof value !== "object") return null;
    return value as ApiErrorPayload;
};

export const getApiErrorCode = (payload: unknown): string | undefined => {
    const parsed = asErrorPayload(payload);
    const nestedCode = nonEmptyString(asErrorPayload(parsed?.error)?.code);
    if (nestedCode === BUDGET_LINE_ACCOUNT_SELECTION_REQUIRED_CODE) return nestedCode;
    return nonEmptyString(parsed?.code) || nestedCode;
};

export const getApiErrorMessage = (payload: unknown, fallback: string): string => {
    const parsed = asErrorPayload(payload);
    if (!parsed) return fallback;

    if (getApiErrorCode(parsed) === EMAIL_ALREADY_EXISTS_CODE) {
        return EMAIL_ALREADY_EXISTS_MESSAGE;
    }

    const nestedError = asErrorPayload(parsed.error);
    const message = nonEmptyString(parsed.message) ||
        nonEmptyString(parsed.error) ||
        nonEmptyString(nestedError?.message) ||
        nonEmptyString(parsed.details) ||
        fallback;
    if (getApiErrorCode(parsed) === BUDGET_LINE_ACCOUNT_SELECTION_REQUIRED_CODE) {
        const accountFailure = [nestedError, parsed].find(value => nonEmptyString(value?.code) === BUDGET_LINE_ACCOUNT_SELECTION_REQUIRED_CODE);
        const accountMessage = nonEmptyString(accountFailure?.message) || nonEmptyString(accountFailure?.error);
        const reason = accountMessage && /חשבו[ןנ]/.test(accountMessage) && /לבחור|בחירה|חלוקה/.test(accountMessage)
            && !/לנסות שוב|נסה שוב|ניסיון נוסף/.test(accountMessage) ? accountMessage
            : "לחברה מספר חשבונות ללא חשבון ראשי. יש לבחור חשבון ראשי או חלוקה מפורשת לפני יצירת השורה התקציבית.";
        return reason.includes(BUDGET_LINE_ACCOUNT_SELECTION_GUIDANCE) ? reason : `${reason} ${BUDGET_LINE_ACCOUNT_SELECTION_GUIDANCE}`;
    }
    return message;
};

// An explicit refusal wins over an outer retryable wrapper. Missing is unknown.
export const getApiErrorRetryable = (payload: unknown): boolean | undefined => {
    const root = asErrorPayload(payload);
    const nested = asErrorPayload(root?.error);
    const values = [root?.retryable, nested?.retryable];
    return values.includes(false) ? false : values.includes(true) ? true : undefined;
};

export class ApiRequestError extends Error {
    readonly status: number;
    readonly code?: string;
    readonly body: unknown;
    readonly retryable?: boolean;

    constructor(payload: unknown, status: number, fallback: string) {
        super(getApiErrorMessage(payload, fallback));
        this.name = "ApiRequestError";
        this.status = status;
        this.code = getApiErrorCode(payload);
        this.body = payload;
        this.retryable = getApiErrorRetryable(payload);
    }
}

export const createApiRequestError = (
    payload: unknown,
    status: number,
    fallback: string
): ApiRequestError => new ApiRequestError(payload, status, fallback);

/* #1013 — שמירה עם גרסת עריכה ישנה (חלון אחר שמר קודם). השרת מחזיר 409 עם אחד הקודים האלה.
   מציגים הודעה אחת בעברית, לא דורסים, ולא מוחקים את מה שהמשתמש הקליד. */
export const EDIT_CONFLICT_MESSAGE = "הנתון השתנה בחלון אחר. רענן כדי לראות את הגרסה העדכנית";
const EDIT_CONFLICT_CODES = new Set([
    "FUTURE_CASHFLOW_TARGET_CHANGED",
    "COMPANY_RECORD_CHANGED",
    "COMPANY_EDIT_CHANGED",
]);

/* codelessConflict: שמירת פרטי החברה מחזירה 409 בלי קוד כשהגרסה ישנה (השרת מסתיר את הקוד בנתיב הזה).
   409 אחרים באותו נתיב (מייל קיים, זהות עסק) מגיעים עם קוד ולכן לא נבלעים. */
export const isEditConflictError = (error: unknown, options: {codelessConflict?: boolean} = {}): boolean => {
    const candidate = error as {status?: unknown; code?: unknown} | null;
    if (candidate?.status !== 409) return false;
    if (typeof candidate.code === "string" && candidate.code) return EDIT_CONFLICT_CODES.has(candidate.code);
    return options.codelessConflict === true;
};

export const editSaveErrorMessage = (error: unknown, fallback: string, options: {codelessConflict?: boolean} = {}): string => {
    if (isEditConflictError(error, options)) return EDIT_CONFLICT_MESSAGE;
    const message = (error as {message?: unknown} | null)?.message;
    return typeof message === "string" && message.trim() ? message : fallback;
};
